import { Monitor, Mail, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

export default function DesktopRequired() {
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();
  const currentUrl = window.location.origin;

  const handleSendLink = async () => {
    if (!email) return;
    
    setIsSending(true);
    try {
      // For now, we'll just copy the link and show a message
      // In the future, this could integrate with an email service
      await navigator.clipboard.writeText(currentUrl);
      toast({
        title: "Link copied!",
        description: "The link has been copied to your clipboard. You can paste it into an email to yourself.",
      });
    } catch (error) {
      toast({
        title: "Couldn't copy link",
        description: "Please manually copy this URL: " + currentUrl,
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader className="pb-4">
          <div className="mx-auto w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Monitor className="w-10 h-10 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Desktop Recommended</CardTitle>
          <CardDescription className="text-base mt-2">
            Radius is optimized for desktop browsers and may not work correctly on mobile devices.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            For the best experience with all features, please access Radius from a desktop or laptop computer.
          </p>

          <div className="space-y-3">
            <Label htmlFor="email" className="text-left block">
              Send yourself a reminder
            </Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={handleSendLink} 
                disabled={!email || isSending}
                size="icon"
                variant="outline"
              >
                <Mail className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              We'll copy the link so you can email it to yourself
            </p>
          </div>

          <div className="pt-4 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => window.location.href = 'https://useradius.io'}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Visit useradius.io
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="mt-6 text-xs text-muted-foreground text-center max-w-sm">
        We're working on improving the mobile experience. Thank you for your patience!
      </p>
    </div>
  );
}
